import { api, ApiError } from '../api';
import { el, errorBanner, skeletonTable, appendAll } from '../components/layout';
import { dataTable } from '../components/table';

type Sub = {
  id: number;
  client_id: number;
  client_name: string;
  leads_per_run: number;
  frequency: string;
  day_of_week: number;
  delivery_email: string;
  icp_profile_id: number | null;
  plan_tier: string;
  is_active: number;
  last_run_at: string | null;
  next_run_at: string | null;
  pending_count?: number;
};

type Delivery = {
  id: number;
  subscription_id: number;
  run_id: number | null;
  status: string;
  lead_count: number | null;
  error: string | null;
  created_at: string;
  sent_at: string | null;
};

type Client = { id: number; name: string; website_url: string; notes: string | null };
type Icp = { id: number; market: string; categories: string; summary: string | null };

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function statusBadge(status: string): HTMLElement {
  const s = String(status || 'pending');
  const map: Record<string, string> = {
    pending: 'badge-queued',
    approved: 'badge-running',
    sent: 'badge-approved',
    failed: 'badge-error',
    skipped: 'badge-queued',
  };
  return el('span', {
    className: `badge ${map[s] || 'badge-queued'}`,
    text: s,
    title: '',
  });
}

function extractEmail(notes: string | null): string {
  if (!notes) return '';
  const m = notes.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return m ? m[0] : '';
}

export function renderSubscriptionsPage(root: HTMLElement) {
  const content = el('div');
  appendAll(
    content,
    el('div', { className: 'page-header' }, [
      el('h1', { text: 'Subscriptions' }),
      el('button', {
        className: 'btn btn-primary',
        type: 'button',
        text: 'New Subscription',
        onClick: () => {
          panelOpen = true;
          paint();
        },
      }),
    ]),
  );

  const host = el('div');
  host.appendChild(skeletonTable());
  content.appendChild(host);
  root.appendChild(content);

  let items: Sub[] = [];
  let clients: Client[] = [];
  let expandedId: number | null = null;
  let deliveries: Delivery[] = [];
  let panelOpen = false;
  let editingId: number | null = null;

  // form state
  let formClientId = '';
  let formEmail = '';
  let formIcpId = '';
  let formLeads = '10';
  let formFreq = 'weekly';
  let formDow = '1';
  let formPlan = 'standard';
  let formActive = true;
  let icps: Icp[] = [];

  async function load() {
    try {
      const [subs, cls] = await Promise.all([
        api<{ items: Sub[] }>('/api/subs'),
        api<{ items: Client[] }>('/api/clients?all=1'),
      ]);
      items = subs.items || [];
      clients = cls.items || [];
      paint();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      host.replaceChildren(errorBanner(msg, load));
    }
  }

  async function loadDeliveries(subId: number) {
    const data = await api<{ items: Delivery[] }>(
      `/api/subscriptions/${subId}/deliveries`,
    );
    deliveries = data.items || [];
  }

  async function loadIcps(clientId: number) {
    try {
      const data = await api<{ items: Icp[] }>(`/api/icp?client_id=${clientId}`);
      icps = data.items || [];
    } catch {
      icps = [];
    }
  }

  function confirmModal(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = el('div', { className: 'modal-overlay' });
      const card = el('div', { className: 'modal-card' });
      appendAll(
        card,
        el('p', { text: message }),
        el('div', { className: 'row-actions' }, [
          el('button', {
            className: 'btn btn-primary',
            type: 'button',
            text: 'Confirm',
            onClick: () => {
              overlay.remove();
              resolve(true);
            },
          }),
          el('button', {
            className: 'btn',
            type: 'button',
            text: 'Cancel',
            onClick: () => {
              overlay.remove();
              resolve(false);
            },
          }),
        ]),
      );
      overlay.appendChild(card);
      overlay.addEventListener('click', (ev) => {
        if (ev.target === overlay) {
          overlay.remove();
          resolve(false);
        }
      });
      document.body.appendChild(overlay);
    });
  }

  function paintPanel(): HTMLElement | null {
    if (!panelOpen) return null;
    const panel = el('aside', { className: 'slide-panel' });
    const close = () => {
      panelOpen = false;
      editingId = null;
      paint();
    };

    const clientSel = el('select', { className: 'select' }) as HTMLSelectElement;
    clientSel.appendChild(el('option', { value: '', text: 'Select client...' }));
    for (const c of clients) {
      const opt = el('option', { value: String(c.id), text: c.name });
      if (formClientId === String(c.id)) opt.setAttribute('selected', '');
      clientSel.appendChild(opt);
    }
    clientSel.addEventListener('change', async () => {
      formClientId = clientSel.value;
      const c = clients.find((x) => String(x.id) === formClientId);
      if (c && !formEmail) formEmail = extractEmail(c.notes);
      if (formClientId) await loadIcps(Number(formClientId));
      paint();
    });

    const emailIn = el('input', {
      className: 'input',
      value: formEmail,
      placeholder: 'hello@laicos.com',
    }) as HTMLInputElement;
    emailIn.addEventListener('input', () => {
      formEmail = emailIn.value;
    });

    const icpSel = el('select', { className: 'select' }) as HTMLSelectElement;
    icpSel.appendChild(el('option', { value: '', text: 'Select ICP...' }));
    for (const icp of icps) {
      const label = `#${icp.id} ${icp.market} ${(icp.categories || '').slice(0, 40)}`;
      const opt = el('option', { value: String(icp.id), text: label });
      if (formIcpId === String(icp.id)) opt.setAttribute('selected', '');
      icpSel.appendChild(opt);
    }
    icpSel.addEventListener('change', () => {
      formIcpId = icpSel.value;
    });

    const leadsIn = el('input', {
      className: 'input',
      type: 'number',
      min: '1',
      max: '50',
      value: formLeads,
    }) as HTMLInputElement;
    leadsIn.addEventListener('input', () => {
      formLeads = leadsIn.value;
    });

    const freqSel = el('select', { className: 'select' }) as HTMLSelectElement;
    for (const f of ['weekly', 'biweekly', 'monthly']) {
      const opt = el('option', { value: f, text: f });
      if (formFreq === f) opt.setAttribute('selected', '');
      freqSel.appendChild(opt);
    }
    freqSel.addEventListener('change', () => {
      formFreq = freqSel.value;
      paint();
    });

    const dowSel = el('select', { className: 'select' }) as HTMLSelectElement;
    DAYS.forEach((d, i) => {
      const opt = el('option', { value: String(i), text: d });
      if (formDow === String(i)) opt.setAttribute('selected', '');
      dowSel.appendChild(opt);
    });
    dowSel.addEventListener('change', () => {
      formDow = dowSel.value;
    });

    const planSel = el('select', { className: 'select' }) as HTMLSelectElement;
    for (const p of ['basic', 'standard', 'premium']) {
      const opt = el('option', { value: p, text: p });
      if (formPlan === p) opt.setAttribute('selected', '');
      planSel.appendChild(opt);
    }
    planSel.addEventListener('change', () => {
      formPlan = planSel.value;
    });

    const activeCb = el('input', { type: 'checkbox' }) as HTMLInputElement;
    activeCb.checked = formActive;
    activeCb.addEventListener('change', () => {
      formActive = activeCb.checked;
    });

    appendAll(
      panel,
      el('div', { className: 'slide-panel-head' }, [
        el('strong', { text: editingId ? `Edit #${editingId}` : 'New Subscription' }),
        el('button', { className: 'btn', type: 'button', text: 'Close', onClick: close }),
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Client' }),
        clientSel,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Delivery email' }),
        emailIn,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'ICP profile' }),
        icpSel,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Leads per run' }),
        leadsIn,
      ]),
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Frequency' }),
        freqSel,
      ]),
    );

    if (formFreq !== 'monthly') {
      appendAll(
        panel,
        el('div', { className: 'field' }, [
          el('label', { className: 'label', text: 'Day of week' }),
          dowSel,
        ]),
      );
    }

    appendAll(
      panel,
      el('div', { className: 'field' }, [
        el('label', { className: 'label', text: 'Plan tier' }),
        planSel,
      ]),
      el('div', { className: 'field check-row' }, [
        el('label', {}, [activeCb, document.createTextNode(' Active')]),
      ]),
      el('button', {
        className: 'btn btn-primary',
        type: 'button',
        text: editingId ? 'Save changes' : 'Create',
        style: 'width:100%;margin-top:8px',
        onClick: async () => {
          try {
            const payload = {
              client_id: Number(formClientId),
              delivery_email: formEmail.trim(),
              icp_profile_id: formIcpId ? Number(formIcpId) : null,
              leads_per_run: Number(formLeads) || 10,
              frequency: formFreq,
              day_of_week: Number(formDow),
              plan_tier: formPlan,
              is_active: formActive,
            };
            if (editingId) {
              await api(`/api/subscriptions/${editingId}`, {
                method: 'PATCH',
                body: JSON.stringify(payload),
              });
            } else {
              await api('/api/subscriptions', {
                method: 'POST',
                body: JSON.stringify(payload),
              });
            }
            panelOpen = false;
            editingId = null;
            await load();
          } catch (err) {
            alert(err instanceof Error ? err.message : String(err));
          }
        },
      }),
    );

    return panel;
  }

  function paint() {
    const wrap = el('div', { className: panelOpen ? 'with-panel' : '' });

    const table = dataTable<Sub>({
      columns: [
        { key: 'client', label: 'Client', render: (s) => s.client_name || '-' },
        { key: 'plan', label: 'Plan', render: (s) => s.plan_tier || '-' },
        {
          key: 'leads',
          label: 'Leads/Run',
          render: (s) => String(s.leads_per_run),
        },
        { key: 'freq', label: 'Frequency', render: (s) => s.frequency },
        { key: 'email', label: 'Delivery Email', render: (s) => s.delivery_email },
        {
          key: 'last',
          label: 'Last Run',
          render: (s) =>
            s.last_run_at
              ? String(s.last_run_at).replace('T', ' ').slice(0, 16)
              : '-',
        },
        {
          key: 'next',
          label: 'Next Run',
          render: (s) =>
            s.next_run_at
              ? String(s.next_run_at).replace('T', ' ').slice(0, 16)
              : '-',
        },
        {
          key: 'status',
          label: 'Status',
          render: (s) =>
            el('span', {
              className: `badge ${Number(s.is_active) === 1 ? 'badge-approved' : 'badge-queued'}`,
              text: Number(s.is_active) === 1 ? 'active' : 'paused',
            }),
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (s) => {
            const row = el('div', { className: 'row-actions' });
            appendAll(
              row,
              el('button', {
                className: 'btn',
                type: 'button',
                text: 'Edit',
                onClick: async () => {
                  editingId = s.id;
                  formClientId = String(s.client_id);
                  formEmail = s.delivery_email;
                  formIcpId = s.icp_profile_id ? String(s.icp_profile_id) : '';
                  formLeads = String(s.leads_per_run);
                  formFreq = s.frequency;
                  formDow = String(s.day_of_week);
                  formPlan = s.plan_tier;
                  formActive = Number(s.is_active) === 1;
                  panelOpen = true;
                  await loadIcps(s.client_id);
                  paint();
                },
              }),
              el('button', {
                className: 'btn',
                type: 'button',
                text: Number(s.is_active) === 1 ? 'Pause' : 'Resume',
                onClick: async () => {
                  await api(`/api/subscriptions/${s.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ is_active: Number(s.is_active) !== 1 }),
                  });
                  await load();
                },
              }),
              el('button', {
                className: 'btn',
                type: 'button',
                text: 'Trigger Now',
                onClick: async () => {
                  const ok = await confirmModal(
                    `Trigger a lead run for ${s.client_name} now?`,
                  );
                  if (!ok) return;
                  try {
                    const out = await api<{
                      ok: boolean;
                      run_id?: number;
                      error?: string;
                    }>(`/api/subscriptions/${s.id}/trigger`, {
                      method: 'POST',
                      body: '{}',
                    });
                    alert(
                      out.ok
                        ? `Run #${out.run_id} queued`
                        : `Failed: ${out.error || 'unknown'}`,
                    );
                    expandedId = s.id;
                    await loadDeliveries(s.id);
                    await load();
                  } catch (err) {
                    alert(err instanceof Error ? err.message : String(err));
                  }
                },
              }),
              el('button', {
                className: 'btn',
                type: 'button',
                text: expandedId === s.id ? 'Hide Deliveries' : 'View Deliveries',
                onClick: async () => {
                  if (expandedId === s.id) {
                    expandedId = null;
                    deliveries = [];
                    paint();
                    return;
                  }
                  expandedId = s.id;
                  await loadDeliveries(s.id);
                  paint();
                },
              }),
            );
            return row;
          },
        },
      ],
      rows: items,
    });

    wrap.appendChild(table);

    if (expandedId != null) {
      const deliveryTable = dataTable<Delivery>({
        columns: [
          {
            key: 'date',
            label: 'Date',
            render: (d) => String(d.created_at || '').replace('T', ' ').slice(0, 19),
          },
          {
            key: 'run',
            label: 'Run ID',
            render: (d) =>
              d.run_id
                ? el('a', {
                    href: `#/runs/${d.run_id}`,
                    target: '_blank',
                    text: String(d.run_id),
                  })
                : '-',
          },
          {
            key: 'leads',
            label: 'Leads',
            render: (d) => String(d.lead_count ?? '-'),
          },
          {
            key: 'status',
            label: 'Status',
            render: (d) => {
              const b = statusBadge(d.status);
              if (d.error) b.title = d.error;
              return b;
            },
          },
          {
            key: 'actions',
            label: 'Actions',
            render: (d) => {
              const row = el('div', { className: 'row-actions' });
              if (d.run_id) {
                appendAll(
                  row,
                  el('a', {
                    className: 'btn',
                    href: `#/runs/${d.run_id}`,
                    target: '_blank',
                    text: 'Review Leads',
                  }),
                );
              }
              if (d.status === 'pending') {
                appendAll(
                  row,
                  el('button', {
                    className: 'btn btn-primary',
                    type: 'button',
                    text: 'Approve & Send',
                    onClick: async () => {
                      const sub = items.find((x) => x.id === expandedId);
                      const ok = await confirmModal(
                        `Send ${d.lead_count ?? 'approved'} leads to ${sub?.delivery_email || 'client'}? This cannot be undone.`,
                      );
                      if (!ok) return;
                      try {
                        await api(`/api/deliveries/${d.id}/approve`, {
                          method: 'POST',
                          body: '{}',
                        });
                        await loadDeliveries(expandedId!);
                        paint();
                      } catch (err) {
                        alert(err instanceof Error ? err.message : String(err));
                      }
                    },
                  }),
                  el('button', {
                    className: 'btn',
                    type: 'button',
                    text: 'Skip',
                    onClick: async () => {
                      await api(`/api/deliveries/${d.id}/skip`, {
                        method: 'POST',
                        body: '{}',
                      });
                      await loadDeliveries(expandedId!);
                      paint();
                    },
                  }),
                );
              }
              return row;
            },
          },
        ],
        rows: deliveries,
      });
      wrap.appendChild(
        el('div', { className: 'card', style: 'margin-top:16px' }, [
          el('div', {
            className: 'step-title',
            text: `Deliveries for subscription #${expandedId}`,
          }),
          deliveryTable,
        ]),
      );
    }

    const panel = paintPanel();
    if (panel) {
      const layout = el('div', { className: 'subs-layout' });
      layout.appendChild(wrap);
      layout.appendChild(panel);
      host.replaceChildren(layout);
    } else {
      host.replaceChildren(wrap);
    }
  }

  load();
}
